import { useCallback } from 'react';

import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import { useActivityBuilderContext } from 'approot/activity-builder/activity-builder.context';
import {
  ActivityBuilderDrawerHeader,
  ActivityBuilderDrawerContent,
  ActivityBuilderDrawerWrapper,
} from 'approot/activity-builder/activity-builder-drawer';
import StimulusUsageMessage from '../shared/stimulus-message';
import {
  SettingsTitleView,
  SettingsRichtextView,
  SettingsThumbnailView,
} from '../shared/settings-view';
import {
  SettingsGroup,
  SettingsLabel,
} from 'approot/activity-builder/settings/settings.style';
import Switch from 'approot/shared/switch/switch';
import { useBatchComponentUpdateContext } from '../shared/batch-component-update.context';
import { ComponentImage } from '../component.constants';
import { getBaseImagePayload } from './image.utils';
import ComponentImageField from '../shared/component-image-field/component-image-field';
import ComponentImageModal from '../shared/component-image-modal/component-image-modal';
import { useFindComponentDimensions } from '../component.hooks';
import { removeTypenameFromObject } from 'approot/shared/logic/remove-typename';

type Props = {
  componentId: string;
  sectionId: string;
  contentId: number;
  componentImage: Omit<ComponentImage, '__typename'>;
};

const ImageEditPreview = ({
  componentId,
  contentId,
  sectionId,
  componentImage,
}: Props) => {
  const { closeDrawer } = useActivityBuilderContext();
  const { isVisible, openModal, closeModal } = useSimpleModalState();
  const { loading, updateComponentProperties } =
    useBatchComponentUpdateContext();

  const { elementRef } = useFindComponentDimensions(componentId);

  const updateCaption = useCallback(
    (
      componentImage: Omit<ComponentImage, '__typename'>,
      showCaption: boolean
    ) => {
      const properties = {
        ...componentImage.properties,
        showCaptionOnLesson: showCaption,
      };
      updateComponentProperties({
        input: {
          sectionId: componentImage.sectionId,
          componentId: componentImage.id,
          componentData: {
            properties: getBaseImagePayload(properties),
          },
        },
        properties,
        typename: 'ActivitiesImageComponent',
      });
    },
    [updateComponentProperties]
  );

  const stimulusId = componentImage.properties.stimulusId;
  const stimulus = componentImage.stimulusResourceProperties;
  const cropData = componentImage.properties.cropData;

  if (!stimulus) return null;

  return (
    <ActivityBuilderDrawerWrapper>
      <ActivityBuilderDrawerHeader title="Image" onClose={closeDrawer} />
      <ActivityBuilderDrawerContent>
        <StimulusUsageMessage
          componentId={componentId}
          stimulusId={stimulusId || 1}
          contentId={contentId}
          sectionId={sectionId}
        />
        <SettingsGroup>
          <SettingsLabel>Image Upload</SettingsLabel>
          {isVisible && elementRef && (
            <ComponentImageModal
              extraContent={
                <StimulusUsageMessage
                  componentId={componentId}
                  stimulusId={stimulusId || 1}
                  contentId={contentId}
                  sectionId={sectionId}
                />
              }
              loading={loading}
              containerDimensions={{
                width: elementRef.offsetWidth,
                height: elementRef.offsetHeight,
              }}
              canFlip={false}
              canUpdateAltText={false}
              canDelete={false}
              canAddSource={false}
              sourceUrl={componentImage.properties.sourceUrl}
              image={stimulus.image}
              altText={stimulus.altText}
              cropData={cropData ? removeTypenameFromObject(cropData) : null}
              closeModal={closeModal}
              onUpdateData={data => {
                updateComponentProperties({
                  input: {
                    sectionId,
                    componentId,
                    componentData: {
                      properties: {
                        stimulusId,
                        sourceUrl: data.sourceUrl,
                        cropData: data.cropData,
                      },
                    },
                  },
                  typename: 'ActivitiesImageComponent',
                  isSaveImmediate: true,
                  onCompleted: () => {
                    closeModal();
                  },
                });
              }}
            />
          )}
          {stimulus.image && (
            <ComponentImageField
              image={{ ...stimulus.image, alt: stimulus.altText }}
              ariaLabel="Edit Image"
              onEdit={openModal}
            />
          )}
        </SettingsGroup>

        <SettingsGroup>
          <SettingsLabel>Image Caption</SettingsLabel>
          <Switch
            data-dropdown-item="true"
            ariaLabel={`Click to ${
              componentImage.properties.showCaptionOnLesson ? 'hide' : 'show'
            } caption`}
            state={componentImage.properties.showCaptionOnLesson}
            onClick={() =>
              updateCaption(
                componentImage,
                !componentImage.properties.showCaptionOnLesson
              )
            }
          />
        </SettingsGroup>
        <SettingsTitleView title="Alt Text" value={stimulus.altText} />
        <SettingsTitleView
          title="Stimulus Name"
          value={stimulus.imageResourceName}
        />
        <SettingsRichtextView
          title="Stimulus Description"
          value={stimulus.imageDescription}
        />
        <SettingsThumbnailView
          title="Stimulus Thumbnail"
          imageUrl={stimulus.coverImage?.responsive?.[0].urls.at(-1)?.url}
        />
      </ActivityBuilderDrawerContent>
    </ActivityBuilderDrawerWrapper>
  );
};

export default ImageEditPreview;
